import { SECTION_LABEL } from 'src/components/actionCards/cardTypography';

// ──────────────────────────────────────────────────────────────────────────────
// launchpadStyles — shared visual tokens + atoms for the Launchpad dashboard.
//
// Centralises the card / header / row / button styling that was previously
// duplicated inline across every panel, so the look stays consistent and is
// tweakable in one place. Pure presentation — no data or business logic here.
// ──────────────────────────────────────────────────────────────────────────────

// ── Vertical rhythm (ONE set of numbers for the whole Launchpad) ───────────────
// 28px between a section's content and the next section label; 10px between a label and its
// content; 16px between cards in any grid. Use these everywhere — no ad-hoc mt / mb.
export const SECTION_GAP = '28px';
export const LABEL_GAP = '10px';
export const GRID_GAP = '16px';

// Palette (kept local to the Launchpad — matches launchpad-c-refined.html)
export const LP = {
    ink: '#0f1d2b',
    teal: '#0b3d3a',
    sub: '#6b7785',
    line: '#f0f3f6',
    blue: '#2f6bff',
    red: '#e5484d',
    redSoft: '#fdecec',
    amber: '#e08a00',
    mint: '#13b67f',
    gold: '#e0a800',
    hover: '#f6f9fb',
};

// Shared easing — a soft "out-back-ish" curve that feels lively but settled.
export const LP_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';

export const twoColumnGridSx = {
    mt: { xs: '18px', md: '24px' },
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
    gap: { xs: '14px', md: '16px', lg: '18px' },
    minWidth: 0,
};

export const fourCardGridSx = {
    display: 'grid',
    gridTemplateColumns: {
        xs: '1fr',
        md: 'repeat(2, minmax(0, 1fr))',
        lg: 'repeat(4, minmax(0, 1fr))',
    },
    gap: GRID_GAP,
    minWidth: 0,
};

export const pipelineGridSx = {
    // position:relative so the stage connectors in LaunchpadPipeline can be absolutely
    // positioned against the whole grid rather than against any single tile — see the
    // comment on that overlay for why they cannot live on the tiles.
    position: 'relative',
    display: { xs: 'flex', md: 'grid' },
    gridTemplateColumns: {
        md: 'repeat(2, minmax(0, 1fr))',
        lg: 'repeat(4, minmax(0, 1fr))',
    },
    gap: { xs: '10px', md: '1px' },
    bgcolor: { xs: 'transparent', md: '#eef2f6' },
    border: { xs: 0, md: '1px solid #e7edf3' },
    borderRadius: '14px',
    overflow: { xs: 'visible', md: 'hidden' },
    boxShadow: '0 1px 2px rgba(15,29,43,0.035)',
    overflowX: { xs: 'auto', md: 'hidden' },
    scrollSnapType: { xs: 'x mandatory', md: 'none' },
    scrollPaddingLeft: { xs: '2px', md: 0 },
    pr: { xs: '18px', md: 0 },
    WebkitOverflowScrolling: 'touch',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': { display: 'none' },
    maxWidth: '100%',
};

export const moneyGridSx = {
    display: 'grid',
    gridTemplateColumns: {
        xs: '1fr',
        md: 'repeat(2, minmax(0, 1fr))',
        lg: 'repeat(4, minmax(0, 1fr))',
    },
    gap: { xs: '10px', md: '1px', lg: '1px' },
    alignItems: 'center',
    background: '#ffffff',
    border: '1px solid #dfe8e5',
    borderRadius: '16px',
    p: { xs: '8px', sm: '10px' },
    boxShadow: '0 1px 2px rgba(15,29,43,0.04), 0 6px 18px rgba(15,29,43,0.05)',
    position: 'relative',
    overflow: 'hidden',
    '& > *:not(:last-of-type)': {
        borderRight: { xs: 0, md: '1px solid #eef1f4' },
        borderBottom: { xs: '1px solid #eef1f4', md: 0 },
    },
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '1px',
        bgcolor: '#13b67f',
    },
};

// Mount reveal: a gentle fade + rise. Apply to each top-level row with a
// staggered delay so the page assembles itself instead of snapping in.
// The @keyframes block is inlined so each consumer is self-contained (emotion
// dedupes identical keyframe names).
export const revealSx = (index = 0) => ({
    '@keyframes lpFadeUp': {
        from: { opacity: 0, transform: 'translateY(10px)' },
        to: { opacity: 1, transform: 'translateY(0)' },
    },
    animation: `lpFadeUp 0.5s ${LP_EASE} both`,
    animationDelay: `${index * 70}ms`,
});

// Outer card for the list panels (Schedule / Tasks / Messages / Reviews).
// Softer, layered shadow gives depth; a hairline border keeps it crisp on
// the #f8fafc page background.
export const panelSx = {
    bgcolor: 'background.paper',
    border: '1px solid #e9edf2',
    borderRadius: '16px',
    p: { xs: '6px 12px 14px', sm: '6px 18px 16px' },
    boxShadow: 'none',
    height: '100%',
    minWidth: 0,
    maxWidth: '100%',
    display: 'flex',
    flexDirection: 'column',
};

// Section eyebrow label (UPPERCASE teal) — used for both top-level row titles
// and the panel headers. icon is an optional leading emoji/string.
export const sectionLabelSx = {
    ...SECTION_LABEL,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
};

// A section header row: the shared label (left) + optional right-side extras (grey badge,
// "vs previous month"), baseline-aligned, LABEL_GAP above the content.
export const sectionHeaderSx = {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: '10px',
    mb: LABEL_GAP,
};

// The header row inside a panel: title (left) + "view all" link (right).
export const panelHeaderSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    flexWrap: 'wrap',
    minWidth: 0,
    pt: { xs: '12px', sm: '14px' },
    pb: '8px',
};

// Right-aligned "Open WhatsApp →" / "All tasks →" link.
export const viewAllSx = {
    fontSize: '11px',
    fontWeight: 700,
    color: LP.blue,
    cursor: 'pointer',
    whiteSpace: 'normal',
    textAlign: 'right',
    flexShrink: 0,
    '&:hover': { textDecoration: 'underline' },
};

export const logLaunchpadError = (section, error, extra = {}) => {
    if (typeof window !== 'undefined' && window.Sentry?.captureException) {
        window.Sentry.captureException(error, {
            tags: { feature: 'launchpad', section },
            extra,
        });
        return;
    }
    // eslint-disable-next-line no-console
    console.error(`[Launchpad:${section}]`, error);
};

// A single list row. Negative inline margin lets the hover tint bleed to the
// panel's inner edges while the divider/content stay aligned.
export const listRowSx = (isFirst) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    minWidth: 0,
    maxWidth: '100%',
    py: '10px',
    px: '8px',
    mx: '-8px',
    borderRadius: '9px',
    borderTop: isFirst ? '1px solid transparent' : `1px solid ${LP.line}`,
    transition: 'background-color 0.14s ease',
    '&:hover': { bgcolor: LP.hover },
});

// Small count chip next to a panel title. Task O: neutral grey, not red — a badge
// is a count, not an alarm; red is reserved for "Overdue" chips on the cards below it.
export const countBadgeSx = {
    fontSize: '11px',
    fontWeight: 800,
    bgcolor: 'action.hover',
    color: 'text.secondary',
    borderRadius: '10px',
    padding: '1px 8px',
    lineHeight: 1.5,
    display: 'inline-block',
};

// The four lower widgets (Schedule, Task Board, WhatsApp, Reviews) share ONE 2×2 grid so
// the cards in a row stretch to equal height (see LaunchpadWidgetShell).
export const widgetGridSx = {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
    alignItems: 'stretch',
    gap: GRID_GAP,
    minWidth: 0,
};
